import axios from 'axios'

// Build settings path from configured base (publicPath)
const BASE_SUFFIX = (process.env.VUE_APP_SELFSUFFIX || '').replace(/\/$/, '')
const SETTINGS_PATH = `${BASE_SUFFIX}/settings/settings.json`

// Optional fallback when settings.json is missing backend_path
const DEFAULT_API_BASE = (process.env.VUE_APP_API_SERVER_URL || '').replace(
  /\/$/,
  ''
)

function normalizeSettingsData(response) {
  let data = response && response.data
  if (typeof data === 'string') {
    // If server returned index.html due to wrong path, this will fail and we fall back
    try {
      data = JSON.parse(data)
    } catch {
      data = {}
    }
  }
  return data || {}
}

function joinUrl(base, path) {
  const b = (base || '').replace(/\/$/, '')
  const p = (path || '').replace(/^\//, '')
  return `${b}/${p}`
}

function normalizeGetParams(config = {}) {
  if (!config || typeof config !== 'object') return {}
  if (config.params && typeof config.params === 'object') return config.params
  return config
}

// Concurrency-safe cached resolver for backend base URL
let cachedBackendBase = null
let backendBasePromise = null

async function resolveBackendBase() {
  // If already resolved, return synchronously
  if (cachedBackendBase) return cachedBackendBase

  // If a request is already in-flight, await it
  if (backendBasePromise) return backendBasePromise

  // Fetch settings once and cache; fallback to env var if missing
  backendBasePromise = axios
    .get(SETTINGS_PATH)
    .then((response) => {
      const data = normalizeSettingsData(response)
      const fromSettings = (data['backend_path'] || '').replace(/\/$/, '')
      const chosen = fromSettings || DEFAULT_API_BASE
      if (!chosen) {
        console.error(
          'Missing backend base URL: settings.backend_path and VUE_APP_API_SERVER_URL are empty'
        )
        throw new Error('Missing backend base URL')
      }
      cachedBackendBase = chosen
      return cachedBackendBase
    })
    .catch((err) => {
      // If settings fetch fails, try env var as last resort
      if (DEFAULT_API_BASE) {
        cachedBackendBase = DEFAULT_API_BASE
        backendBasePromise = null
        return cachedBackendBase
      }
      // Reset promise on failure to allow future retries
      backendBasePromise = null
      throw err
    })

  return backendBasePromise
}

// Helper function to get Auth0 token (in-memory)
async function getAuth0Token() {
  const vue = window.Vue || null
  const auth0 = vue && vue.prototype ? vue.prototype.$auth0 : null
  if (auth0 && auth0.isAuthenticated) {
    try {
      const token = await auth0.getTokenSilently()
      return token
    } catch (error) {
      return null
    }
  }
  return null
}

const api = {
  async post(endpoint, payload) {
    const backendBase = await resolveBackendBase()
    axios.defaults.headers.post['accept'] = 'application/json'

    // Prefer Auth0 token; rely on interceptor otherwise
    const auth0Token = await getAuth0Token()
    if (auth0Token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth0Token}`
      console.log('Using Auth0 token for POST request')
    }

    return axios.post(joinUrl(backendBase, endpoint), payload)
  },
  async postBlob(endpoint, payload = null) {
    // POST expecting a binary (e.g., PDF). Includes Auth0 token if available.
    const backendBase = await resolveBackendBase()
    const auth0Token = await getAuth0Token()
    if (auth0Token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth0Token}`
    }
    const url = joinUrl(backendBase, endpoint)
    return axios.post(url, payload, { responseType: 'blob' })
  },
  async put(endpoint, payload) {
    const backendBase = await resolveBackendBase()
    axios.defaults.headers.put['accept'] = 'application/json'

    const auth0Token = await getAuth0Token()
    if (auth0Token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth0Token}`
      console.log('Using Auth0 token for PUT request')
    }

    return axios.put(joinUrl(backendBase, endpoint), payload)
  },
  async get(endpoint, config = {}) {
    const backendBase = await resolveBackendBase()
    axios.defaults.headers.get['accept'] = 'application/json'

    const auth0Token = await getAuth0Token()
    if (auth0Token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth0Token}`
    }
    //else {
    //  console.log('No Auth0 token available for GET request to:', endpoint)
    //}

    const params = normalizeGetParams(config)
    const queryString = new URLSearchParams(params).toString()
    const url = `${joinUrl(backendBase, endpoint)}${
      queryString ? `?${queryString}` : ''
    }`
    return axios.get(url)
  },
  async delete(endpoint, payload = null) {
    const backendBase = await resolveBackendBase()
    axios.defaults.headers.delete['accept'] = 'application/json'

    const auth0Token = await getAuth0Token()
    if (auth0Token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth0Token}`
      console.log('Using Auth0 token for DELETE request')
    }

    // Support sending data in DELETE request body
    return axios.delete(joinUrl(backendBase, endpoint), {
      data: payload
    })
  },
  async getSelf(endpoint) {
    return axios.get('.' + endpoint)
  },
  async getSettings() {
    return axios.get(SETTINGS_PATH)
  }
}

export default api
